import { useEffect, useState } from 'react'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import { cancelDefaultClaim } from '../../services/residentApi'
import { RequiredAsterisk } from '../RequiredAsterisk'
import { ResidentDialogShell } from './ResidentDialogShell'

const MAX_REASON = 200

/** Mirrors Angular `DefaultClaimComponent` cancel claim flow (`resident-edit-new` → defaultClaim). */
export function CancelReimbursementRequestDialog({
  isOpen,
  defaultId,
  onClose,
  onUpdated,
}: {
  isOpen: boolean
  defaultId?: string | number | null
  onClose: () => void
  onUpdated: () => void | Promise<void>
}) {
  const [reasonNote, setReasonNote] = useState('')
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setReasonNote('')
    setProcessing(false)
  }, [isOpen])

  const trimmed = reasonNote.trim()
  const canProceed = trimmed.length > 0 && reasonNote.length <= MAX_REASON

  const submit = async () => {
    if (!defaultId || !canProceed) return
    setProcessing(true)
    try {
      await cancelDefaultClaim(defaultId, {
        default_claim: 'cancel_claim',
        reason_note: trimmed,
        email_sendto: 'default_manager',
        send_to: 'liberty',
        email_sendtoproperty: 'property',
      })
      showSuccess('Default Reimbursement Updated Successfully')
      await onUpdated()
      onClose()
    } catch (err) {
      showApiError(err)
    } finally {
      setProcessing(false)
    }
  }

  return (
    <ResidentDialogShell
      isOpen={isOpen}
      title="Cancel Reimbursement Request"
      headerVariant="centeredTitle"
      onClose={onClose}
      panelDataTestId="cancel-reimbursement-dialog-panel"
      footer={
        <div
          className="flex flex-col-reverse gap-3 sm:flex-row sm:flex-wrap sm:justify-center"
          data-testid="cancel-reimbursement-dialog-footer"
        >
          <button
            type="button"
            disabled={!canProceed || processing}
            onClick={() => void submit()}
            data-testid="cancel-reimbursement-dialog-proceed-button"
            className="w-full min-w-[120px] rounded-lg bg-button px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:brightness-110 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 disabled:shadow-none disabled:hover:brightness-100 sm:w-auto"
          >
            {processing ? 'Please wait…' : 'Proceed'}
          </button>
          <button
            type="button"
            disabled={processing}
            onClick={onClose}
            data-testid="cancel-reimbursement-dialog-exit-button"
            className="w-full min-w-[120px] rounded-lg bg-button px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            Cancel
          </button>
        </div>
      }
    >
      <div
        className="space-y-4 text-sm text-slate-900"
        data-testid="cancel-reimbursement-dialog-body"
      >
        <p className="text-center font-semibold text-[#2c3e50]">
          <strong>Please read the following before cancelling a Reimbursement Request:</strong>
        </p>
        <ul className="list-disc space-y-2 pl-5 leading-relaxed">
          <li>
            DO NOT cancel a Reimbursement Request to change the Reimbursement Type (Eviction or Skipped). Instead,
            send a message to the Reimbursement Team via the Message Board to request the change.
          </li>
          <li>A Reimbursement Request should only be cancelled if the resident has Paid in Full.</li>
          <li>
            Cancelling a Reimbursement Request will permanently delete previous Reimbursement related information and
            any uploaded files.
          </li>
          <li>A confirmation email will be sent to the Liberty Rent Reimbursement team, and to the property.</li>
        </ul>

        <div>
          <label htmlFor="cancel-reimbursement-reason" className="mb-1 block text-[13px] font-medium text-slate-600">
            Please enter your reason for cancelling the Reimbursement Request
            <RequiredAsterisk />
            <span className="font-normal text-slate-500">
              {reasonNote ? ` (${MAX_REASON - reasonNote.length} char left)` : ` (${MAX_REASON} char max)`}
            </span>
          </label>
          <textarea
            id="cancel-reimbursement-reason"
            rows={4}
            maxLength={MAX_REASON}
            value={reasonNote}
            onChange={(e) => setReasonNote(e.target.value.slice(0, MAX_REASON))}
            data-testid="cancel-reimbursement-dialog-reason-textarea"
            className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-[#0091ff] focus:ring-2 focus:ring-sky-100"
            placeholder=""
          />
        </div>
      </div>
    </ResidentDialogShell>
  )
}
